import classNames from "classnames/bind"
import style from './index.module.scss'
import { useTranslation } from "react-i18next"
import { Form, notification } from "antd"
import { useCallback, useEffect, useMemo, useState } from "react"
import { getUser } from "utils/index"
import shopApi from "api/shop"
import revenueApi from "api/revenue"
import moment from "moment"
import { RevenueFilter, RevenueTable, AdminRevenueFilter, AdminRevenueTable, AdminBudgetTable } from "./components"
import holidayApi from "api/holiday"
import { rolesGetAllShop, rolesAdminRevenue } from "./roles"

const cx = classNames.bind(style)
export default function RevenuePage() {
    const [formSearch] = Form.useForm()
    const { t } = useTranslation()
    const [api, contextHolder] = notification.useNotification();

    const [shops, setShops] = useState([])
    const [loading, setLoading] = useState(false)
    const [revenueInfo, setRevenueInfo] = useState(null)
    const [budgetInfo, setBudgetInfo] = useState(null)
    const [listHoliday, setListHoliday] = useState([])

    const openNotificationWithIcon = (type, message, description) => {
        api[type]({ message, description });
    };

    const isAdminRevenue = rolesAdminRevenue.includes(+getUser().role)

    const shopInfo = useMemo(() => {
        return shops.find((shop) => shop.value === formSearch.getFieldValue('shop'))
    }, [shops, formSearch, formSearch.getFieldValue('shop')])

    // Admin multi-shop search
    const onSearchAdmin = async (values) => {
        try {
            setLoading(true)
            const monthVal = values?.month || formSearch.getFieldValue('month')
            const monthStr = moment(monthVal).format('YYYY-MM')
            const [revenueRes, budgetRes] = await Promise.all([
                revenueApi.getMultiShopRevenue({ month: monthStr }),
                revenueApi.getMultiShopBudget({ month: monthStr }),
            ])
            setRevenueInfo(revenueRes)
            setBudgetInfo(budgetRes)
        } catch (error) {
            // handle error
        } finally {
            setLoading(false)
        }
    }

    // Per-shop search (non-admin)
    const onSearch = async (values = formSearch.getFieldsValue()) => {
        try {
            setLoading(true)
            const res = await revenueApi.getByMonth({
                ...values,
                month: moment(values.month).format('YYYY-MM')
            })
            setRevenueInfo(res)
        } catch (error) {
            // handle error
        } finally {
            setLoading(false)
        }
    }

    // Fetch shops (non-admin)
    const getStore = useCallback(async () => {
        try {
            const res = await shopApi.getShopByRole();
            if (rolesGetAllShop.includes(+getUser().role)) {
                setShops(
                    res.shops.map((shop) => ({
                        shopCode: shop.shopCode,
                        value: shop.shopId,
                        label: shop.shopName,
                        cabinet_code_1: shop.cabinet_code_1,
                        cabinet_code_2: shop.cabinet_code_2,
                    }))
                );
            } else {
                setShops(
                    res.shops.map((shop) => ({
                        shopCode: shop.shop.shop_code,
                        value: shop.shop._id,
                        label: shop.shop.name,
                        cabinet_code_1: shop.shop.cabinet_code_1,
                        cabinet_code_2: shop.shop.cabinet_code_2,
                    }))
                );

                formSearch.setFieldsValue({
                    shop: res.shops[0].shop._id
                });
            }
        } catch (error) {
            // handle error
        }
    }, [isAdminRevenue]);

    // Fetch holidays
    const fetchHoliday = async () => {
        const { holidays } = await holidayApi.getAll()
        setListHoliday(holidays)
    }

    useEffect(() => {
        if (!isAdminRevenue) {
            getStore()
        }
        fetchHoliday()
    }, [isAdminRevenue])

    // Admin view
    if (isAdminRevenue) {
        return (
            <>
                {contextHolder}
                <div className={cx("title")}>
                    <p>{t('revenue')}</p>
                </div>
                <AdminRevenueFilter form={formSearch} onFinish={onSearchAdmin} loading={loading} />
                {
                    revenueInfo &&
                    <AdminRevenueTable
                        shops={revenueInfo.shops}
                        month={revenueInfo.month}
                        listHoliday={listHoliday}
                    />
                }
                {
                    budgetInfo &&
                    <AdminBudgetTable
                        budgets={budgetInfo.shops}
                        month={revenueInfo.month}
                        onReload={() => onSearchAdmin({})}
                    />
                }
            </>
        )
    }

    // Non-admin view (existing)
    return (
        <>
            {contextHolder}
            <div className={cx("title")}>
                <p>{t('revenue')}</p>
            </div>
            <RevenueFilter form={formSearch} shops={shops} onFinish={onSearch} loading={loading} />
            {
                revenueInfo &&
                <RevenueTable
                    shop={formSearch.getFieldValue('shop')}
                    shopInfo={shopInfo}
                    notify={openNotificationWithIcon}
                    listHoliday={listHoliday}
                    revenueInfo={revenueInfo}
                    onSearch={onSearch}
                    month={moment(formSearch.getFieldValue('month')).format('YYYY-MM')} />
            }
        </>
    )
}
